import React, { memo, useContext, useEffect, useState } from 'react';

import getIn from '../../components/connectedForm/utils/getIn';
import { IFormatterProps } from '../../@types/workspaceTypes';
import FormModalFormatter from './formatters/FormModalFormatter';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { AlertType, DocumentType } from '../../graphql/schema/p4types';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { makeStyles } from '@fluentui/react-components';

//I am unaware of a means to directly cast between this union type and the array below, so any additional supported type
// must also be added below.
type IFinancialTransactionModalSupportedTypes =
  | DocumentType.SalesInvoice
  | DocumentType.SalesCredit
  | DocumentType.SalesReceipt
  | DocumentType.SalesRefund
  | DocumentType.SalesOpeningBalanceInvoice
  | DocumentType.SalesOpeningBalanceCredit
  | DocumentType.SalesBadDebt
  | DocumentType.NominalJournal
  | DocumentType.NominalAccrual
  | DocumentType.NominalPrepayment
  | DocumentType.BankPayment
  | DocumentType.BankReceipt
  | DocumentType.BankTransfer
  | DocumentType.SalesVatInvoice
  | DocumentType.SalesVatCredit
  | DocumentType.SalesAllocation
  | DocumentType.PurchaseCredit
  | DocumentType.PurchaseVatInvoice
  | DocumentType.PurchaseVatCredit
  | DocumentType.PurchasePayment
  | DocumentType.PurchaseRefund
  | DocumentType.PurchaseInvoice
  | DocumentType.PurchaseOpeningBalanceInvoice
  | DocumentType.PurchaseOpeningBalanceCredit
  | DocumentType.PurchaseAllocation
  | DocumentType.StockAdjustment
  | DocumentType.GoodsReceipt;

//I am unaware of a means to directly cast between this array and the union type above, so any additional supported type
// must also be added above.
const FINANCIAL_TRANSACTION_MODAL_SUPPORTED_TYPES: Array<IFinancialTransactionModalSupportedTypes> = [
  DocumentType.SalesInvoice,
  DocumentType.SalesCredit,
  DocumentType.SalesReceipt,
  DocumentType.SalesRefund,
  DocumentType.SalesOpeningBalanceInvoice,
  DocumentType.SalesOpeningBalanceCredit,
  DocumentType.SalesBadDebt,
  DocumentType.NominalJournal,
  DocumentType.NominalAccrual,
  DocumentType.NominalPrepayment,
  DocumentType.BankPayment,
  DocumentType.BankReceipt,
  DocumentType.BankTransfer,
  DocumentType.SalesVatInvoice,
  DocumentType.SalesVatCredit,
  DocumentType.SalesAllocation,
  DocumentType.PurchaseCredit,
  DocumentType.PurchaseVatInvoice,
  DocumentType.PurchaseVatCredit,
  DocumentType.PurchasePayment,
  DocumentType.PurchaseRefund,
  DocumentType.PurchaseInvoice,
  DocumentType.PurchaseOpeningBalanceInvoice,
  DocumentType.PurchaseOpeningBalanceCredit,
  DocumentType.PurchaseAllocation,
  DocumentType.StockAdjustment,
  DocumentType.GoodsReceipt,
];

const LazySalesInvoiceForm = React.lazy(() => import('../../views/salesLedger/SalesInvoiceForm'));
const LazySalesCreditForm = React.lazy(() => import('../../views/salesLedger/salesCredit/SalesCreditForm'));
const LazySalesReceiptForm = React.lazy(() => import('../../views/salesLedger/SalesReceiptForm'));
const LazySalesRefundForm = React.lazy(() => import('../../views/salesLedger/SalesRefundForm'));
const LazySalesOpeningBalanceForm = React.lazy(() => import('../../views/salesLedger/SalesOpeningBalanceForm'));
const LazySalesBadDebtForm = React.lazy(() => import('../../views/salesLedger/SalesBadDebtForm'));
const LazyNominalJournal = React.lazy(() => import('../../views/nominal/NominalJournalForm'));
const LazyNominalAccrual = React.lazy(() => import('../../views/nominal/NominalAccrualPrepaymentForm'));
const LazyBankPaymentReceipt = React.lazy(() => import('../../views/bank/BankPaymentReceiptForm'));
const LazySalesVatOnlyForm = React.lazy(() => import('../../views/salesLedger/SalesVatOnlyForm'));
const LazyPurchaseInvoiceForm = React.lazy(() => import('../../views/supplier/PurchaseInvoiceForm'));
const LazyPurchaseCreditForm = React.lazy(() => import('../../views/supplier/PurchaseCreditForm'));
const LazyBankTransferForm = React.lazy(() => import('../../views/bank/BankTransferForm'));
const LazyPurchaseVatOnlyForm = React.lazy(() => import('../../views/supplier/PurchaseVatOnlyForm'));
const LazyPurchaseRefundForm = React.lazy(() => import('../../views/supplier/PurchaseRefundForm'));
const LazyPurchaseOpeningBalanceForm = React.lazy(() => import('../../views/supplier/PurchaseOpeningBalanceForm'));
const LazyStockAdjustmentForm = React.lazy(() => import('../../views/product/ProductsStockAdjustmentForm'));
const LazyGoodsReceiptForm = React.lazy(() => import('../../views/product/ProductsGoodsReceiptForm'));
const LazySalesAllocationForm = React.lazy(() => import('../../views/salesLedger/SalesAllocationForm'));
const LazyPurchaseAllocationForm = React.lazy(() => import('../../views/supplier/PurchaseAllocationForm'));
const LazyPurchasePaymentForm = React.lazy(() => import('../../views/supplier/PurchasePaymentForm'));

const useStyles = makeStyles({
  link: {
    textAlign: 'right',
  },
});

/**
 * Intended as a drop-in replacement for the combination of 'DeprecatedModalFormatter' with 'FinancialTransactionModal'.
 * Only been tested for Sales Receipts (As of writing) - Would encourage use, but test appropriately.
 * @param value - To display in the cell (Typically the document number).
 * @param dependentValues - Standard metaData for formatters (Access to rowData, additional options, etc).
 */
export const FinancialTransactionModalFormatter = memo<IFormatterProps>(({ value, dependentValues }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { dispatchSimpleToast } = useP4ToastController();
  const { entity } = useContext(ConnectedFormContext);

  const styles = useStyles();
  const { options, rowData } = dependentValues;

  let idValue: Maybe<number>;
  let documentType: IFinancialTransactionModalSupportedTypes;
  if (options?.documentType) {
    documentType = options.documentType;
    idValue = getIn(rowData, `${options?.entityPath}.${options?.entityId}`) ?? getIn(rowData, options?.entityId);
  } else if (options?.entityPath) {
    idValue = getIn(rowData, `${options?.entityPath}.${options?.entityId}`);
    documentType = getIn(rowData, `${options?.entityPath}.documentType`);
  } else {
    idValue = getIn(rowData, options?.entityId);
    documentType = getIn(rowData, 'documentType');
  }

  useEffect(() => {
    //This logic is not expected to trigger, but is here to catch any unusual exceptions and handle it better than 'the modal displays a blank white space'
    if (!idValue && modalOpen) {
      setModalOpen(false);
      dispatchSimpleToast('Source could not be displayed', 'The id to display could not be found', AlertType.Error);
    }
  }, [dispatchSimpleToast, idValue, modalOpen]);

  return (
    <FormModalFormatter
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      value={value}
      hasModalTarget={
        documentType &&
        entity?.documentType !== documentType &&
        FINANCIAL_TRANSACTION_MODAL_SUPPORTED_TYPES.includes(documentType)
      }
      className={styles.link}
    >
      <FinancialTransactionModal
        forceReadOnly={true}
        onClose={() => setModalOpen(false)}
        pageView={true}
        match={{ params: { id: idValue } }}
        overrideParams={{ id: idValue }}
        entityPath={dependentValues.options?.entityPath}
        rowData={dependentValues.rowData}
        parentType={dependentValues.options.parentType}
        documentType={documentType}
      />
    </FormModalFormatter>
  );
});

// eslint-disable-next-line complexity
const FinancialTransactionModal = ({ entityPath, rowData, documentType: overrideDocumentType, ...props }: any) => {
  const documentType: IFinancialTransactionModalSupportedTypes =
    overrideDocumentType ?? getIn(rowData, entityPath ? `${entityPath}.documentType` : 'documentType');

  switch (documentType) {
    case DocumentType.SalesInvoice:
      return <LazySalesInvoiceForm {...props} />;
    case DocumentType.SalesCredit:
      return <LazySalesCreditForm {...props} />;
    case DocumentType.SalesReceipt:
      return <LazySalesReceiptForm {...props} />;
    case DocumentType.SalesRefund:
      return <LazySalesRefundForm {...props} />;
    case DocumentType.SalesOpeningBalanceInvoice:
      return <LazySalesOpeningBalanceForm {...props} />;
    case DocumentType.SalesOpeningBalanceCredit:
      return <LazySalesOpeningBalanceForm {...props} />;
    case DocumentType.SalesBadDebt:
      return <LazySalesBadDebtForm {...props} />;
    case DocumentType.NominalJournal:
      return <LazyNominalJournal {...props} />;
    case DocumentType.NominalAccrual:
      return <LazyNominalAccrual {...props} />;
    case DocumentType.NominalPrepayment:
      return <LazyNominalAccrual {...props} />;
    case DocumentType.BankPayment:
      return <LazyBankPaymentReceipt {...props} />;
    case DocumentType.BankReceipt:
      return <LazyBankPaymentReceipt {...props} />;
    case DocumentType.BankTransfer:
      return <LazyBankTransferForm {...props} />;
    case DocumentType.SalesVatInvoice:
      return <LazySalesVatOnlyForm {...props} />;
    case DocumentType.SalesVatCredit:
      return <LazySalesVatOnlyForm {...props} />;
    case DocumentType.PurchaseInvoice:
      return <LazyPurchaseInvoiceForm {...props} />;
    case DocumentType.PurchaseCredit:
      return <LazyPurchaseCreditForm {...props} />;
    case DocumentType.PurchaseVatInvoice:
      return <LazyPurchaseVatOnlyForm {...props} />;
    case DocumentType.PurchaseVatCredit:
      return <LazyPurchaseVatOnlyForm {...props} />;
    case DocumentType.PurchasePayment:
      return <LazyPurchasePaymentForm {...props} />;
    case DocumentType.PurchaseRefund:
      return <LazyPurchaseRefundForm {...props} />;
    case DocumentType.PurchaseOpeningBalanceInvoice:
      return <LazyPurchaseOpeningBalanceForm {...props} />;
    case DocumentType.PurchaseOpeningBalanceCredit:
      return <LazyPurchaseOpeningBalanceForm {...props} />;
    case DocumentType.StockAdjustment:
      return <LazyStockAdjustmentForm {...props} />;
    case DocumentType.GoodsReceipt:
      return <LazyGoodsReceiptForm {...props} />;
    case DocumentType.SalesAllocation:
      return <LazySalesAllocationForm {...props} />;
    case DocumentType.PurchaseAllocation:
      return <LazyPurchaseAllocationForm {...props} />;
    default:
      return null;
  }
};

export default FinancialTransactionModal;
